import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchCorrelacion, fetchCorrelacionCompare,
  exportCorrelacionExcel, exportCorrelacionPDF,
} from '@/services/api';
import { ChartCard } from '@/components/dashboard/ChartCard';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertTriangle, TrendingUp, Activity, Users, RefreshCw,
  FileSpreadsheet, FileText, GitCompare,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, Cell, Legend,
} from 'recharts';

// ── Constants ─────────────────────────────────────────────────────────────────
const RISK_COLOR = {
  alta:  { bg: 'rgba(198,40,40,0.12)',  text: '#EF5350', border: 'rgba(198,40,40,0.25)',  label: 'ALTA' },
  media: { bg: 'rgba(230,81,0,0.12)',   text: '#FF7043', border: 'rgba(230,81,0,0.25)',   label: 'MEDIA' },
  baja:  { bg: 'rgba(0,153,64,0.12)',   text: '#4CAF50', border: 'rgba(0,153,64,0.25)',   label: 'BAJA' },
  sin_datos: { bg: 'rgba(120,144,156,0.12)', text: '#90A4AE', border: 'rgba(120,144,156,0.25)', label: 'SIN DATOS' },
};
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];
const COLORS_A = '#4A9EFF';
const COLORS_B = '#F59E0B';

// ── Sub-components ────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, icon: Icon, color }) {
  return (
    <div className="rounded-xl p-4 flex flex-col gap-2"
      style={{ background: 'var(--vz-bg-card)', border: '1px solid var(--vz-border-subtle)', boxShadow: 'var(--vz-shadow-card)' }}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-montserrat font-semibold uppercase tracking-wider" style={{ color: 'var(--vz-text-muted)' }}>{label}</p>
        {Icon && (
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${color}18` }}>
            <Icon size={14} style={{ color }} />
          </div>
        )}
      </div>
      <p className="font-montserrat font-bold text-2xl" style={{ color: 'var(--vz-text-primary)' }}>{value ?? '—'}</p>
      {sub && <p className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>{sub}</p>}
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl p-3 text-xs shadow-xl" style={{ background: '#0D1B2E', border: '1px solid rgba(74,158,255,0.25)' }}>
      <p className="font-bold mb-2 text-white">{label}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.fill }} />
          <span style={{ color: 'var(--vz-text-muted)' }}>{p.name}:</span>
          <span className="font-bold" style={{ color: p.fill }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
}

// ── Single-year bar chart ─────────────────────────────────────────────────────
function SingleYearChart({ rows, year }) {
  const data = rows.slice(0, 20).map((r) => ({
    name: r.gerente.length > 22 ? r.gerente.slice(0, 22) + '…' : r.gerente,
    OPS: r.total_ops,
    Liderazgo: r.total_lid,
  }));
  if (!data.length) return <p className="text-center text-xs py-10" style={{ color: 'var(--vz-text-muted)' }}>Sin datos para {year}</p>;
  return (
    <ResponsiveContainer width="100%" height={Math.max(320, data.length * 28)}>
      <BarChart data={data} layout="vertical" margin={{ left: 12, right: 24, top: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
        <XAxis type="number" tick={{ fill: '#6B7280', fontSize: 10 }} axisLine={false} tickLine={false} />
        <YAxis type="category" dataKey="name" tick={{ fill: '#9CA3AF', fontSize: 10 }} width={160} axisLine={false} tickLine={false} />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(74,158,255,0.05)' }} />
        <Legend wrapperStyle={{ fontSize: 10, color: '#9CA3AF', paddingTop: 8 }} />
        <Bar dataKey="OPS" fill={COLORS_A} radius={[0, 4, 4, 0]} maxBarSize={14} />
        <Bar dataKey="Liderazgo" fill="#4CAF50" radius={[0, 4, 4, 0]} maxBarSize={14} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Comparison bar chart ──────────────────────────────────────────────────────
function CompareChart({ comparison, yearA, yearB, metric }) {
  const keyA = `${metric}_${yearA}`;
  const keyB = `${metric}_${yearB}`;
  const label = metric === 'ops' ? 'OPS' : metric === 'lid' ? 'Liderazgo' : 'IF';

  const data = comparison.slice(0, 12).map((r) => ({
    name: r.gerente.length > 16 ? r.gerente.slice(0, 16) + '…' : r.gerente,
    [yearA]: r[keyA] || 0,
    [yearB]: r[keyB] || 0,
  }));
  if (!data.length) return <p className="text-center text-xs py-10" style={{ color: 'var(--vz-text-muted)' }}>Sin datos</p>;
  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
        <XAxis type="number" tick={{ fill: '#6B7280', fontSize: 10 }} axisLine={false} tickLine={false} />
        <YAxis type="category" dataKey="name" tick={{ fill: '#9CA3AF', fontSize: 9 }} width={115} axisLine={false} tickLine={false} />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(74,158,255,0.05)' }} />
        <Legend wrapperStyle={{ fontSize: 10, color: '#9CA3AF', paddingTop: 8 }} />
        <Bar dataKey={String(yearA)} name={`${label} ${yearA}`} fill={COLORS_A} radius={[0, 4, 4, 0]} maxBarSize={10} />
        <Bar dataKey={String(yearB)} name={`${label} ${yearB}`} fill={COLORS_B} radius={[0, 4, 4, 0]} maxBarSize={10} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Correlacion() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [compareMode, setCompareMode] = useState(false);
  const [yearB, setYearB] = useState(CURRENT_YEAR - 1);
  const [compareMetric, setCompareMetric] = useState('ops');
  const [exporting, setExporting] = useState(null);

  // Single-year query
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['correlacion', year],
    queryFn: () => fetchCorrelacion(year),
    staleTime: 120000,
    enabled: !compareMode,
  });

  // Comparison query
  const { data: compareData, isLoading: compareLoading } = useQuery({
    queryKey: ['correlacion-compare', year, yearB],
    queryFn: () => fetchCorrelacionCompare(year, yearB),
    staleTime: 120000,
    enabled: compareMode,
  });

  const handleExport = async (type) => {
    setExporting(type);
    try {
      const blob = type === 'excel' ? await exportCorrelacionExcel(year) : await exportCorrelacionPDF(year);
      const ext  = type === 'excel' ? 'xlsx' : 'pdf';
      const mime = type === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf';
      const url = URL.createObjectURL(new Blob([blob], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `correlacion_${year}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Export ${type.toUpperCase()} descargado`);
    } catch {
      toast.error('Error al exportar');
    } finally {
      setExporting(null);
    }
  };

  const hasSiniest = rows.length > 0 && rows[0]?.has_siniest !== false;
  const alta  = rows.filter(r => r.risk_level === 'alta').length;
  const media = rows.filter(r => r.risk_level === 'media').length;
  // Weighted by hours worked — a plain mean of per-gerente IF over-weights
  // small zones with few hours and skews the headline number.
  const totalHoras   = rows.reduce((s, r) => s + (r.horas || 0), 0);
  const totalAccBaja = rows.reduce((s, r) => s + (r.acc_baja || 0), 0);
  const avgIF = hasSiniest && totalHoras > 0
    ? ((totalAccBaja * 1_000_000) / totalHoras).toFixed(1)
    : '—';

  const comparison = compareData?.comparison || [];

  return (
    <div className="flex flex-col min-h-0 h-full overflow-auto" style={{ background: 'var(--vz-bg-primary)' }}>
      <div className="flex-1 p-4 lg:p-6 space-y-5">

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-montserrat font-bold text-lg" style={{ color: 'var(--vz-text-primary)' }}>
              Correlación entre Módulos
            </h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
              Compara el Índice de Frecuencia de siniestralidad con la actividad OPS y Liderazgo por gerente de zona
            </p>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Year A selector */}
            <div className="flex items-center gap-1">
              <span className="text-[10px] font-medium" style={{ color: 'var(--vz-text-muted)' }}>Año{compareMode ? ' A:' : ':'}</span>
              {YEAR_OPTIONS.map((y) => (
                <button key={y} onClick={() => setYear(y)} data-testid={`year-btn-${y}`}
                  className="px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all"
                  style={year === y
                    ? { background: 'rgba(74,158,255,0.2)', color: '#4A9EFF', border: '1px solid rgba(74,158,255,0.5)' }
                    : { background: 'var(--vz-bg-elevated)', color: 'var(--vz-text-muted)', border: '1px solid var(--vz-border-subtle)' }
                  }>
                  {y}
                </button>
              ))}
            </div>

            {/* Year B selector (compare mode only) */}
            {compareMode && (
              <div className="flex items-center gap-1">
                <span className="text-[10px] font-medium" style={{ color: 'var(--vz-text-muted)' }}>Año B:</span>
                {YEAR_OPTIONS.filter((y) => y !== year).map((y) => (
                  <button key={y} onClick={() => setYearB(y)} data-testid={`yearb-btn-${y}`}
                    className="px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all"
                    style={yearB === y
                      ? { background: 'rgba(245,158,11,0.2)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.5)' }
                      : { background: 'var(--vz-bg-elevated)', color: 'var(--vz-text-muted)', border: '1px solid var(--vz-border-subtle)' }
                    }>
                    {y}
                  </button>
                ))}
              </div>
            )}

            {/* Compare toggle */}
            <button
              onClick={() => setCompareMode((v) => !v)}
              data-testid="toggle-compare-mode"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
              style={compareMode
                ? { background: 'rgba(245,158,11,0.18)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.4)' }
                : { background: 'var(--vz-bg-elevated)', color: 'var(--vz-text-muted)', border: '1px solid var(--vz-border-subtle)' }
              }
            >
              <GitCompare size={13} />
              {compareMode ? `${year} vs ${yearB}` : 'Comparar años'}
            </button>

            {/* Exports (single year only) */}
            {!compareMode && (
              <>
                <button data-testid="export-correlacion-excel" onClick={() => handleExport('excel')}
                  disabled={exporting === 'excel' || isLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                  style={{ background: 'rgba(0,180,90,0.12)', color: '#00C853', border: '1px solid rgba(0,180,90,0.25)' }}>
                  {exporting === 'excel' ? <RefreshCw size={13} className="animate-spin" /> : <FileSpreadsheet size={13} />}
                  Excel
                </button>
                <button data-testid="export-correlacion-pdf" onClick={() => handleExport('pdf')}
                  disabled={exporting === 'pdf' || isLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                  style={{ background: 'rgba(220,50,50,0.12)', color: '#EF5350', border: '1px solid rgba(220,50,50,0.25)' }}>
                  {exporting === 'pdf' ? <RefreshCw size={13} className="animate-spin" /> : <FileText size={13} />}
                  PDF
                </button>
              </>
            )}
          </div>
        </div>

        {/* Notice: OPS-only fallback */}
        {!compareMode && rows.length > 0 && !hasSiniest && (
          <div className="flex items-start gap-3 p-3 rounded-lg border" style={{ background: 'rgba(255,165,0,0.08)', borderColor: 'rgba(255,165,0,0.3)' }}>
            <span className="text-xs" style={{ color: '#FFA500' }}>
              Los datos de Siniestralidad no están importados — mostrando OPS + Charlas por gerente. Importa el Excel de Siniestralidad para ver IF, IG y niveles de riesgo.
            </span>
          </div>
        )}

        {/* ── SINGLE YEAR MODE ── */}
        {!compareMode && (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <KpiCard label="Gerentes Analizados" value={isLoading ? '…' : rows.length} icon={Users} color="var(--vz-blue)" />
              <KpiCard label="IF Promedio" value={isLoading ? '…' : avgIF} sub="Acc/millón horas" icon={Activity} color="var(--vz-orange)" />
              <KpiCard label="Riesgo Alto" value={isLoading ? '…' : alta} sub="IF > 50" icon={AlertTriangle} color="var(--vz-red)" />
              <KpiCard label="Riesgo Medio" value={isLoading ? '…' : media} sub="IF entre 20 y 50" icon={TrendingUp} color="var(--vz-yellow)" />
            </div>

            {/* Bar chart — full width */}
            <ChartCard title={`OPS y Liderazgo — ${year}`} subtitle={`Top ${Math.min(15, rows.length)} gerentes por IF`}>
              {isLoading
                ? <div className="flex items-center justify-center h-48"><RefreshCw size={20} className="animate-spin" style={{ color: 'var(--vz-green)' }} /></div>
                : <SingleYearChart rows={[...rows].sort((a, b) => (b['if'] || 0) - (a['if'] || 0)).slice(0, 15)} year={year} />}
            </ChartCard>

            {/* Table — full width below chart */}
            <ChartCard title="Tabla de Correlación Detallada" subtitle={`Siniestralidad vs OPS / Liderazgo — ${year}`}>
              <CorrelacionTable rows={rows} isLoading={isLoading} />
            </ChartCard>
          </>
        )}

        {/* ── COMPARISON MODE ── */}
        {compareMode && (
          <>
            {/* Metric selector */}
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-medium" style={{ color: 'var(--vz-text-muted)' }}>Ver métrica:</span>
              {[{ key: 'ops', label: 'OPS' }, { key: 'lid', label: 'Liderazgo' }, { key: 'if', label: 'IF Accidentes' }].map((m) => (
                <button key={m.key} onClick={() => setCompareMetric(m.key)}
                  className="px-3 py-1 rounded-lg text-[11px] font-semibold transition-all"
                  style={compareMetric === m.key
                    ? { background: 'rgba(74,158,255,0.2)', color: '#4A9EFF', border: '1px solid rgba(74,158,255,0.4)' }
                    : { background: 'var(--vz-bg-elevated)', color: 'var(--vz-text-muted)', border: '1px solid var(--vz-border-subtle)' }
                  }>
                  {m.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              {/* Comparison chart */}
              <ChartCard
                title={`${compareMetric === 'ops' ? 'OPS' : compareMetric === 'lid' ? 'Liderazgo' : 'IF'} — ${year} vs ${yearB}`}
                subtitle="Top 12 gerentes por zona"
                className="lg:col-span-3"
              >
                {compareLoading
                  ? <div className="flex items-center justify-center h-60"><RefreshCw size={20} className="animate-spin" style={{ color: 'var(--vz-blue)' }} /></div>
                  : <CompareChart comparison={comparison} yearA={year} yearB={yearB} metric={compareMetric} />}
              </ChartCard>

              {/* Summary table */}
              <ChartCard title="Resumen por Gerente" subtitle={`${year} vs ${yearB}`} className="lg:col-span-2">
                <ScrollArea className="h-72">
                  <Table>
                    <TableHeader>
                      <TableRow style={{ borderColor: 'var(--vz-border-subtle)' }}>
                        <TableHead className="text-[9px] font-montserrat uppercase" style={{ color: 'var(--vz-text-muted)' }}>Gerente</TableHead>
                        <TableHead className="text-[9px] font-montserrat uppercase text-right" style={{ color: COLORS_A }}>OPS {year}</TableHead>
                        <TableHead className="text-[9px] font-montserrat uppercase text-right" style={{ color: COLORS_B }}>OPS {yearB}</TableHead>
                        <TableHead className="text-[9px] font-montserrat uppercase text-right" style={{ color: 'var(--vz-text-muted)' }}>Δ</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {compareLoading ? (
                        <TableRow><TableCell colSpan={4} className="text-center text-xs py-6" style={{ color: 'var(--vz-text-muted)' }}><RefreshCw size={14} className="animate-spin inline mr-1" />Cargando…</TableCell></TableRow>
                      ) : comparison.map((r, i) => {
                        const a = r[`ops_${year}`] || 0;
                        const b = r[`ops_${yearB}`] || 0;
                        const delta = b > 0 ? Math.round(((a - b) / b) * 100) : null;
                        return (
                          <TableRow key={i} className="hover:bg-[#132860] transition-colors" style={{ borderColor: 'var(--vz-border-subtle)' }}>
                            <TableCell className="text-[10px] max-w-[120px]">
                              <span className="block truncate font-medium" style={{ color: 'var(--vz-text-primary)' }} title={r.gerente}>{r.gerente}</span>
                            </TableCell>
                            <TableCell className="text-[11px] font-bold text-right" style={{ color: COLORS_A }}>{a}</TableCell>
                            <TableCell className="text-[11px] font-bold text-right" style={{ color: COLORS_B }}>{b}</TableCell>
                            <TableCell className="text-[10px] text-right">
                              {delta !== null ? (
                                <span style={{ color: delta >= 0 ? '#4CAF50' : '#EF5350' }}>
                                  {delta >= 0 ? '+' : ''}{delta}%
                                </span>
                              ) : <span style={{ color: 'var(--vz-text-muted)' }}>—</span>}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </ChartCard>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Extracted table component ─────────────────────────────────────────────────
function CorrelacionTable({ rows, isLoading }) {
  return (
    <>
      <ScrollArea className="h-[480px]">
        <Table>
          <TableHeader>
            <TableRow style={{ borderColor: 'var(--vz-border-subtle)' }}>
              {['Gerente Zona', 'IF', 'IG', 'Acc. Baja', 'OPS Total', 'OPS/FTE', 'Lid. Total', 'Lid./FTE', 'Riesgo'].map(h => (
                <TableHead key={h} className="text-[9px] font-montserrat uppercase tracking-wider sticky top-0"
                  style={{ color: 'var(--vz-text-muted)', background: 'var(--vz-bg-card)' }}>
                  {h}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-xs py-10" style={{ color: 'var(--vz-text-muted)' }}>
                  <RefreshCw size={16} className="animate-spin inline mr-2" />Cargando...
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-xs py-10" style={{ color: 'var(--vz-text-muted)' }}>
                  Sin datos. Importa el Excel de Siniestralidad para ver la correlación completa.
                </TableCell>
              </TableRow>
            ) : rows.map((r, i) => {
              const rc = RISK_COLOR[r.risk_level] || RISK_COLOR.baja;
              return (
                <TableRow key={i} className="transition-colors" style={{ borderColor: 'var(--vz-border-subtle)' }} data-testid="correlacion-row">
                  <TableCell className="text-xs font-medium max-w-[140px] truncate" style={{ color: 'var(--vz-text-primary)' }}>{r.gerente}</TableCell>
                  <TableCell className="text-xs font-bold" style={{ color: rc.text }}>{r['if']}</TableCell>
                  <TableCell className="text-xs" style={{ color: 'var(--vz-orange)' }}>{r.ig}</TableCell>
                  <TableCell className="text-xs" style={{ color: 'var(--vz-text-secondary)' }}>{r.acc_baja}</TableCell>
                  <TableCell className="text-xs" style={{ color: 'var(--vz-blue)' }}>{r.total_ops}</TableCell>
                  <TableCell className="text-xs" style={{ color: 'var(--vz-text-secondary)' }}>{r.ops_por_fte}</TableCell>
                  <TableCell className="text-xs" style={{ color: 'var(--vz-green)' }}>{r.total_lid}</TableCell>
                  <TableCell className="text-xs" style={{ color: 'var(--vz-text-secondary)' }}>{r.lid_por_fte}</TableCell>
                  <TableCell>
                    <span className="text-[9px] font-bold px-2 py-0.5 rounded-full"
                      style={{ background: rc.bg, color: rc.text, border: `1px solid ${rc.border}` }}>
                      {rc.label}
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ScrollArea>
      {rows.length > 0 && (
        <div className="flex items-center gap-4 px-2 pt-3 border-t" style={{ borderColor: 'var(--vz-border-subtle)' }}>
          {Object.entries(RISK_COLOR).map(([key, rc]) => (
            <div key={key} className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full inline-block" style={{ background: rc.text }} />
              <span className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>
                {rc.label}{key === 'alta' ? ' (IF>50)' : key === 'media' ? ' (IF 20-50)' : key === 'baja' ? ' (IF<20)' : ' (sin horas importadas)'}
              </span>
            </div>
          ))}
          <span className="ml-auto text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>FTE = Jornadas/1742h</span>
        </div>
      )}
    </>
  );
}
